/**
 * Shadow camera fitting (render space, tested): an orthographic light-space square around a sphere, snapped to
 * whole texels in the light's own basis. Shared by the key-light rig and every shadow clipmap level (shadows.ts).
 * Allocation-free.
 */

/** Result of fitting the key light's orthographic shadow camera around a sphere. */
export interface ShadowFit {
  /** Light target (the snapped focus centre), render space. */
  tx: number
  ty: number
  tz: number
  /** Light position (the shadow camera's eye), render space. */
  px: number
  py: number
  pz: number
  /** Orthographic half extent (metres), near and far planes along the light direction. */
  half: number
  near: number
  far: number
  /** World size of one shadow-map texel. */
  texel: number
}

export function createShadowFit(): ShadowFit {
  return { tx: 0, ty: 0, tz: 0, px: 0, py: 0, pz: 1, half: 1, near: 0, far: 2, texel: 0 }
}

/**
 * Fits the shadow camera around the sphere (c, radius) for the light direction d (toward the light, any length).
 * The centre is snapped to whole texels in the shadow camera's own basis (the same basis `Matrix4.lookAt`
 * builds with up +Y), so a focus that moves by less than a texel does not make shadow edges crawl. The eye sits
 * `radius + margin` toward the light, so casters up to `margin` beyond the sphere still land in the map.
 * Allocation-free.
 */
export function fitShadowFocus(
  dx: number,
  dy: number,
  dz: number,
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  mapSize: number,
  margin: number,
  out: ShadowFit,
): ShadowFit {
  let zl = Math.sqrt(dx * dx + dy * dy + dz * dz)
  let zx = zl > 0 ? dx / zl : 0
  let zy = zl > 0 ? dy / zl : 0
  let zz = zl > 0 ? dz / zl : 1
  // x = up × z with up = +Y; degenerate when z is (anti)parallel to up: perturb like Matrix4.lookAt.
  let xx = zz
  let xy = 0
  let xz = -zx
  let xl = Math.sqrt(xx * xx + xy * xy + xz * xz)
  if (xl < 1e-9) {
    zz += 0.0001
    zl = Math.sqrt(zx * zx + zy * zy + zz * zz)
    zx /= zl
    zy /= zl
    zz /= zl
    xx = zz
    xz = -zx
    xl = Math.sqrt(xx * xx + xy * xy + xz * xz)
  }
  xx /= xl
  xy /= xl
  xz /= xl
  // y = z × x
  const yx = zy * xz - zz * xy
  const yy = zz * xx - zx * xz
  const yz = zx * xy - zy * xx
  const texel = mapSize > 0 ? (2 * radius) / mapSize : 0
  let sx = 0
  let sy = 0
  if (texel > 0) {
    const u = cx * xx + cy * xy + cz * xz
    const v = cx * yx + cy * yy + cz * yz
    sx = Math.round(u / texel) * texel - u
    sy = Math.round(v / texel) * texel - v
  }
  out.tx = cx + xx * sx + yx * sy
  out.ty = cy + xy * sx + yy * sy
  out.tz = cz + xz * sx + yz * sy
  const back = radius + margin
  out.px = out.tx + zx * back
  out.py = out.ty + zy * back
  out.pz = out.tz + zz * back
  out.half = radius
  out.near = 0
  out.far = back + radius
  out.texel = texel
  return out
}
